/**
 * Machinery — Platform Admin страница справочника техники (AK-235).
 *
 * Список моделей с фильтром по группе / типу / производителю. Тонкий wrapper
 * над ReferenceCatalogList + MachineryEditorDrawer (поля editor'а меняются по
 * выбранной группе — полиморф из fields_schema группы). Delete = архивирование.
 */
import { useMemo, useState } from 'react';
import { Archive, MoreHorizontal, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceCatalogList,
  type ReferenceColumn,
} from '@/components/reference/ReferenceCatalogList';
import { MachineryEditorDrawer } from './MachineryEditorDrawer';
import {
  useArchiveMachineryModel,
  useCreateMachineryModel,
  useMachineryGroups,
  useMachineryModelsInfinite,
  useMachineryTypes,
  useManufacturers,
  useUpdateMachineryModel,
} from './hooks/useMachineryQueries';
import {
  machineryGroupLabel,
  type MachineryModelListItem,
  type MachineryModelListQuery,
  type MachineryModelPayload,
} from './machineryCatalogTypes';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'моделей';
  if (m10 === 1) return 'модель';
  if (m10 >= 2 && m10 <= 4) return 'модели';
  return 'моделей';
}

function yearLabel(from: number | null, to: number | null): string {
  if (from && to) return `${from}–${to}`;
  if (from) return `с ${from}`;
  if (to) return `до ${to}`;
  return '—';
}

export function PlatformAdminMachineryPage() {
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [manufacturerFilter, setManufacturerFilter] = useState<string>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<MachineryModelListItem | null>(null);

  const groupsQ = useMachineryGroups();
  const groups = groupsQ.data?.items ?? [];
  const typesQ = useMachineryTypes(groupFilter);
  const types = typesQ.data?.items ?? [];
  const manufacturersQ = useManufacturers();
  const manufacturers = manufacturersQ.data?.items ?? [];

  const query: MachineryModelListQuery = useMemo(
    () => ({
      search: search.trim() || undefined,
      machinery_group: groupFilter === 'all' ? undefined : groupFilter,
      machinery_type_id: typeFilter === 'all' ? undefined : typeFilter,
      manufacturer_id: manufacturerFilter === 'all' ? undefined : manufacturerFilter,
    }),
    [search, groupFilter, typeFilter, manufacturerFilter],
  );

  const modelsQ = useMachineryModelsInfinite(query);
  const createMut = useCreateMachineryModel();
  const updateMut = useUpdateMachineryModel();
  const archiveMut = useArchiveMachineryModel();

  const items = modelsQ.data?.items ?? [];
  const total = modelsQ.data?.total ?? 0;

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const handleEdit = (m: MachineryModelListItem) => {
    setEditing(m);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload: MachineryModelPayload) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, payload });
      toast.success(`Модель «${payload.name_translations.ru}» сохранена`);
    } else {
      await createMut.mutateAsync(payload);
      toast.success(`Модель «${payload.name_translations.ru}» создана`);
    }
  };

  const handleArchive = async (m: MachineryModelListItem) => {
    if (!window.confirm(`Архивировать «${m.name_translations.ru}»?`)) return;
    try {
      await archiveMut.mutateAsync(m.id);
      toast.success('Архивировано.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const columns: ReferenceColumn<MachineryModelListItem>[] = [
    {
      key: 'code',
      header: 'Код МДМ',
      width: '120px',
      render: (m) => (
        <span className="font-mono text-xs text-muted-foreground">{m.code ?? '—'}</span>
      ),
    },
    {
      key: 'name',
      header: 'Название',
      render: (m) => (
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => handleEdit(m)}
            className="text-left font-medium text-foreground hover:text-primary"
          >
            {m.name_translations.ru ?? '—'}
          </button>
          {m.also_known_as.length > 0 ? (
            <div className="text-xs text-muted-foreground">
              Другие названия: {m.also_known_as.join(', ')}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'group',
      header: 'Группа',
      width: '160px',
      render: (m) => <Badge variant="outline">{machineryGroupLabel(m.machinery_group)}</Badge>,
    },
    {
      key: 'type',
      header: 'Тип',
      width: '150px',
      render: (m) =>
        m.type_name ? (
          <Badge variant="secondary">{m.type_name}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'manufacturer',
      header: 'Производитель',
      width: '160px',
      render: (m) => (
        <span className="text-sm text-muted-foreground">{m.manufacturer_name ?? '—'}</span>
      ),
    },
    {
      key: 'years',
      header: 'Годы',
      width: '110px',
      render: (m) => (
        <span className="text-sm text-muted-foreground">{yearLabel(m.year_from, m.year_to)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      align: 'right',
      render: (m) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Действия для ${m.name_translations.ru}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => handleEdit(m)}>
              <Pencil className="h-4 w-4" />
              Редактировать
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => void handleArchive(m)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <PlatformAdminLayout>
      <ReferenceCatalogList<MachineryModelListItem>
        title="Техника"
        subtitle={`Системный справочник сельхозтехники · ${total} ${plural(total)}`}
        createLabel="Создать модель"
        onCreate={handleCreate}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по названию, коду МДМ или другому названию…"
        items={items}
        isLoading={modelsQ.isLoading}
        isError={modelsQ.isError}
        error={modelsQ.error ?? undefined}
        onRetry={() => modelsQ.refetch()}
        getRowKey={(m) => m.id}
        total={total}
        unitLabel="моделей"
        hasMore={!!modelsQ.hasNextPage}
        loadingMore={modelsQ.isFetchingNextPage}
        onLoadMore={() => modelsQ.fetchNextPage()}
        columns={columns}
        emptyTitle="Пока нет ни одной модели техники"
        emptyHint="Создайте первую модель — выберите группу (самоходная / прицепная / авиационная), и редактор покажет характеристики под эту группу."
        toolbarSlot={
          <>
            <Select
              value={groupFilter}
              onValueChange={(v) => {
                setGroupFilter(v);
                setTypeFilter('all');
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Группа" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все группы</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.code}>
                    {g.name_translations.ru ?? g.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Тип" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все типы</SelectItem>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name_translations.ru ?? t.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={manufacturerFilter} onValueChange={setManufacturerFilter}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Производитель" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все производители</SelectItem>
                {manufacturers.map((mf) => (
                  <SelectItem key={mf.id} value={mf.id}>
                    {mf.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <MachineryEditorDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        mode={editing ? 'edit' : 'create'}
        model={editing}
        groups={groups}
        manufacturers={manufacturers}
        onSubmit={handleSubmit}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminMachineryPage;
